import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";

import { getRol, getToken, getUser } from "../../../api/Account";
import { getProyectedDefeatedAsync } from "../../../api/Report";
import { getGestoresAsync } from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import { formatearFecha, formatearMonto } from "../../../helpers/formato";
import ReporteAgrupado from "../../../components/reportes/ReporteAgrupado";

const TODOS = "t";
// Enlaces anteriores: el último segmento de la ruta era 0 = por nombre, 1 = por días de atraso.
const ORDEN_POR_ATRASO = 1;

const dinero = (key, label) => ({
  key,
  label,
  align: "center",
  total: true,
  formatoTotal: formatearMonto,
  valor: (c) => formatearMonto(c[key]),
  excel: (c) => c[key],
});

const fecha = (key, label) => ({
  key,
  label,
  align: "center",
  valor: (c) => formatearFecha(c[key]),
  excel: (c) => formatearFecha(c[key]),
});

// Créditos que vencen en el rango. "Atraso" es lo atrasado a hoy y "Saldo Total" lo que
// queda por cobrar; antes la columna "Saldo Total" mostraba el atraso.
const COLUMNAS = [
  { key: "id", label: "#", align: "center", valor: (c) => c.id, excel: (c) => c.id },
  fecha("fechaEntrega", "F.Entrega"),
  {
    key: "fullName",
    label: "Nombre Completo",
    align: "left",
    texto: true,
    valor: (c) => c.fullName,
  },
  dinero("montoEntregado", "Monto Entregado"),
  fecha("fechaVencimiento", "F.Vencimiento"),
  {
    key: "atrasoDias",
    label: "Días Atraso",
    align: "center",
    valor: (c) => c.atrasoDias,
    excel: (c) => c.atrasoDias,
  },
  dinero("atraso", "Atraso"),
  dinero("saldoTotal", "Saldo Total"),
];

const VencimientosProyectados = () => {
  const [data, setData] = useState([]);
  const [gestorName, setGestorName] = useState("Todos");

  const { selectedGestor, fechaDesde, fechaHasta, selectedSortBy } = useParams();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const manejarError = useCallback(
    (result) => {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
    },
    [setIsUnautorized]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [reporte, gestores] = await Promise.all([
        getProyectedDefeatedAsync(token, {
          desde: fechaDesde.toString(),
          hasta: fechaHasta.toString(),
          selectedSortBy,
          gestorId: selectedGestor === TODOS ? 0 : selectedGestor,
        }),
        selectedGestor === TODOS ? null : getGestoresAsync(token),
      ]);
      setIsLoading(false);

      if (!reporte.statusResponse) {
        manejarError(reporte);
        return;
      }
      setData(reporte.data.result);

      if (selectedGestor !== TODOS) {
        // Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
        const gestor =
          gestores && gestores.statusResponse
            ? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
            : null;
        setGestorName(
          gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
        );
      }
    })();
  }, [
    token,
    selectedGestor,
    fechaDesde,
    fechaHasta,
    selectedSortBy,
    setIsLoading,
    manejarError,
  ]);

  // El servidor manda el nombre del gestor del crédito: también el de uno dado de baja.
  const agruparPor = useCallback((fila) => fila.gestorNombre, []);

  return (
    <ReporteAgrupado
      tituloReporte="Reporte de Vencimientos Proyectados"
      nombreArchivo="VencimientosProyectados"
      parametros={[
        { label: "Gestor Seleccionado", valor: gestorName },
        { label: "Desde", valor: formatearFecha(fechaDesde) },
        { label: "Hasta", valor: formatearFecha(fechaHasta) },
      ]}
      columnas={COLUMNAS}
      data={data}
      agruparPor={agruparPor}
      ordenInicial={
        parseInt(selectedSortBy) === ORDEN_POR_ATRASO ? "atrasoDias" : "fullName"
      }
      contexto={{ title, slogan, icon, user, rol }}
    />
  );
};

export default VencimientosProyectados;
